import { useState, useEffect, useCallback } from 'react';

// 게임 상태 타입
export type GameStatus = 'idle' | 'playing' | 'paused' | 'ended';

// 장애물 타입
export interface Obstacle {
  id: string;
  position: number;
  height: number;
}

// 적 타입
export interface Enemy {
  id: string;
  position: number;
  health: number;
}

// 아이템 타입
export interface Item {
  id: string;
  position: number;
  type: 'coin' | 'powerup';
}

// 발사체 타입
export interface Projectile {
  id: string;
  position: { x: number; y: number };
}

// 게임 훅
export const useGame = () => {
  // 게임 상태
  const [status, setStatus] = useState<GameStatus>('idle');
  // 점수
  const [score, setScore] = useState<number>(0);
  // 최고 점수
  const [highScore, setHighScore] = useState<number>(0);
  // 코인
  const [coins, setCoins] = useState<number>(0);
  // 총 코인
  const [totalCoins, setTotalCoins] = useState<number>(0);
  // 특수 능력 게이지
  const [specialMeter, setSpecialMeter] = useState<number>(0);
  // 특수 능력 활성화 여부
  const [specialActive, setSpecialActive] = useState<boolean>(false);
  
  // 캐릭터 상태
  const [isJumping, setIsJumping] = useState<boolean>(false);
  const [isSliding, setIsSliding] = useState<boolean>(false);
  
  // 게임 요소
  const [obstacles, setObstacles] = useState<Obstacle[]>([]);
  const [enemies, setEnemies] = useState<Enemy[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [projectiles, setProjectiles] = useState<Projectile[]>([]);
  
  // 게임 속도 (픽셀/프레임)
  const [gameSpeed, setGameSpeed] = useState<number>(5);
  
  // 게임 타이머
  const [gameTimer, setGameTimer] = useState<NodeJS.Timeout | null>(null);
  
  // 로컬 스토리지에서 데이터 불러오기
  useEffect(() => {
    const savedHighScore = localStorage.getItem('ninjaRunnerHighScore');
    const savedTotalCoins = localStorage.getItem('ninjaRunnerTotalCoins');
    
    if (savedHighScore) {
      setHighScore(parseInt(savedHighScore, 10));
    }
    
    if (savedTotalCoins) {
      setTotalCoins(parseInt(savedTotalCoins, 10));
    }
  }, []);
  
  // 게임 시작
  const startGame = useCallback(() => {
    setStatus('playing');
    setScore(0);
    setCoins(0);
    setSpecialMeter(0);
    setSpecialActive(false);
    setIsJumping(false);
    setIsSliding(false);
    setObstacles([]);
    setEnemies([]);
    setItems([]);
    setProjectiles([]);
    setGameSpeed(5);
    
    // 게임 루프 시작
    const timer = setInterval(() => {
      // 점수 증가
      setScore(prev => prev + 1);
      
      // 게임 속도 증가 (점수에 따라)
      if (score > 0 && score % 500 === 0) {
        setGameSpeed(prev => Math.min(prev + 1, 15));
      }
      
      // 특수 능력 게이지 자동 증가
      if (!specialActive) {
        setSpecialMeter(prev => Math.min(prev + 0.1, 100));
      }
      
      // 장애물, 적, 아이템 생성 로직은 별도 함수로 구현
      
    }, 100);
    
    setGameTimer(timer);
    
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [score, specialActive]);
  
  // 게임 일시정지
  const pauseGame = useCallback(() => {
    if (status === 'playing') {
      setStatus('paused');
      if (gameTimer) {
        clearInterval(gameTimer);
        setGameTimer(null);
      }
    } else if (status === 'paused') {
      setStatus('playing');
      startGame();
    }
  }, [status, gameTimer, startGame]);
  
  // 게임 종료
  const endGame = useCallback(() => {
    setStatus('ended');
    
    if (gameTimer) {
      clearInterval(gameTimer);
      setGameTimer(null);
    }
    
    // 최고 점수 업데이트
    if (score > highScore) {
      setHighScore(score);
      localStorage.setItem('ninjaRunnerHighScore', score.toString());
    }
    
    // 총 코인 업데이트
    const newTotalCoins = totalCoins + coins;
    setTotalCoins(newTotalCoins);
    localStorage.setItem('ninjaRunnerTotalCoins', newTotalCoins.toString());
  }, [score, highScore, coins, totalCoins, gameTimer]);
  
  // 점프 처리
  const jump = useCallback(() => {
    if (!isJumping && !isSliding && status === 'playing') {
      setIsJumping(true);
      
      // 점프 후 착지
      setTimeout(() => {
        setIsJumping(false);
      }, 500);
    }
  }, [isJumping, isSliding, status]);
  
  // 슬라이드 처리
  const slide = useCallback(() => {
    if (!isJumping && !isSliding && status === 'playing') {
      setIsSliding(true);
      
      // 슬라이드 종료
      setTimeout(() => {
        setIsSliding(false);
      }, 500);
    }
  }, [isJumping, isSliding, status]);
  
  // 공격 처리
  const attack = useCallback(() => {
    if (status === 'playing') {
      // 새 발사체 생성
      const newProjectile: Projectile = {
        id: `projectile-${Date.now()}`,
        position: { x: 110, y: isSliding ? 15 : 30 } // 캐릭터 위치 기준
      };
      
      setProjectiles(prev => [...prev, newProjectile]);
    }
  }, [status, isSliding]);
  
  // 특수 능력 사용
  const useSpecialAbility = useCallback(() => {
    if (status === 'playing' && specialMeter >= 100) {
      setSpecialActive(true);
      setSpecialMeter(0);
      
      // 특수 능력 지속 시간
      setTimeout(() => {
        setSpecialActive(false);
      }, 5000);
    }
  }, [status, specialMeter]);
  
  // 장애물 생성
  const createObstacle = useCallback(() => {
    if (status === 'playing' && Math.random() < 0.05) {
      const newObstacle: Obstacle = {
        id: `obstacle-${Date.now()}`,
        position: window.innerWidth,
        height: Math.floor(Math.random() * 30) + 30
      };
      
      setObstacles(prev => [...prev, newObstacle]);
    }
  }, [status]);
  
  // 적 생성
  const createEnemy = useCallback(() => {
    if (status === 'playing' && Math.random() < 0.03) {
      const newEnemy: Enemy = {
        id: `enemy-${Date.now()}`,
        position: window.innerWidth,
        health: 1
      };
      
      setEnemies(prev => [...prev, newEnemy]);
    }
  }, [status]);
  
  // 아이템 생성
  const createItem = useCallback(() => {
    if (status === 'playing' && Math.random() < 0.02) {
      const newItem: Item = {
        id: `item-${Date.now()}`,
        position: window.innerWidth,
        type: Math.random() < 0.8 ? 'coin' : 'powerup'
      };
      
      setItems(prev => [...prev, newItem]);
    }
  }, [status]);
  
// 헬퍼: 두 사각형 충돌 판정 함수
const isCollidingRect = (
  a: { x: number; y: number; width: number; height: number },
  b: { x: number; y: number; width: number; height: number }
) => {
  return (
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y
  );
};

// [4] 발사체와 obstacle 충돌 감지: 충돌한 장애물과 발사체 모두 제거
const handleProjectileObstacleCollisions = () => {
  const collidedObstacles = new Set<string>();
  const collidedProjectiles = new Set<string>();

  // 발사체와 장애물 간 충돌 검사
  projectiles.forEach((projectile) => {
    const projectileRect = {
      x: projectile.position.x,
      y: projectile.position.y,
      width: 15,
      height: 5,
    };

    obstacles.forEach((obstacle) => {
      const obstacleRect = {
        x: obstacle.position,
        y: 0,
        width: 30,
        height: obstacle.height,
      };

      if (isCollidingRect(projectileRect, obstacleRect)) {
        collidedObstacles.add(obstacle.id);
        collidedProjectiles.add(projectile.id);
      }
    });
  });

  // 상태 업데이트: 충돌한 장애물 제거
  setObstacles((prevObstacles) =>
    prevObstacles.filter((obstacle) => !collidedObstacles.has(obstacle.id))
  );
  // 상태 업데이트: 충돌한 발사체 제거
  setProjectiles((prevProjectiles) =>
    prevProjectiles.filter((proj) => !collidedProjectiles.has(proj.id))
  );
};


// 충돌 감지
const detectCollisions = useCallback(() => {
  if (status !== 'playing') return;
  
  const characterRect = {
    x: 50,
    y: isJumping ? 120 : 0,
    width: 60,
    height: isSliding ? 30 : 60
  };
  
  // [1] obstacle과 캐릭터 충돌 감지: 충돌 시 게임 종료
  for (const obstacle of obstacles) {
    const obstacleRect = {
      x: obstacle.position,
      y: 0,
      width: 30,
      height: obstacle.height
    };
    
    if (isCollidingRect(characterRect, obstacleRect)) {
      endGame();
      return;
    }
  }
  
  // [2] enemy와 캐릭터 충돌 감지: 충돌 시 게임 종료
  for (const enemy of enemies) {
    const enemyRect = {
      x: enemy.position,
      y: 0,
      width: 40,
      height: 40
    };
    
    if (isCollidingRect(characterRect, enemyRect)) {
      endGame();
      return;
    }
  }
  
  // [3] 아이템 충돌 감지 (기존 코드 유지)
  setItems(prev =>
    prev.filter(item => {
      const itemRect = {
        x: item.position,
        y: 40,
        width: 20,
        height: 20
      };
      
      if (isCollidingRect(characterRect, itemRect)) {
        if (item.type === 'coin') {
          setCoins(prev => prev + 1);
        } else if (item.type === 'powerup') {
          setSpecialMeter(prev => Math.min(prev + 20, 100));
        }
        return false;
      }
      return true;
    })
  );
  
  // [4] 발사체와 obstacle 충돌 감지: 발사체와 충돌한 장애물 모두 제거
  handleProjectileObstacleCollisions();
  
  // [5] 발사체와 enemy 충돌 감지 (기존 코드 유지)
  for (const projectile of projectiles) {
    const projectileRect = {
      x: projectile.position.x,
      y: projectile.position.y,
      width: 15,
      height: 5
    };
    
    let enemyHit = false;
    setEnemies(prev =>
      prev.filter(enemy => {
        const enemyRect = {
          x: enemy.position,
          y: 0,
          width: 40,
          height: 40
        };
        if (isCollidingRect(projectileRect, enemyRect)) {
          enemyHit = true;
          return false; // enemy 제거
        }
        return true;
      })
    );
    if (enemyHit) {
      setProjectiles(prev => prev.filter(p => p.id !== projectile.id));
    }
  }
  
  // 캐릭터와 발사체 간의 충돌은 고려하지 않음
}, [status, isJumping, isSliding, obstacles, enemies, items, projectiles, endGame]);


  // 게임 요소 업데이트
  const updateGameElements = useCallback(() => {
    if (status !== 'playing') return;
    
    // 장애물 업데이트
    setObstacles(prev => 
      prev
        .map(obstacle => ({
          ...obstacle,
          position: obstacle.position - gameSpeed
        }))
        .filter(obstacle => obstacle.position > -50)
    );
    
    // 적 업데이트
    setEnemies(prev => 
      prev
        .map(enemy => ({
          ...enemy,
          position: enemy.position - gameSpeed
        }))
        .filter(enemy => enemy.position > -50)
    );
    
    // 아이템 업데이트
    setItems(prev => 
      prev
        .map(item => ({
          ...item,
          position: item.position - gameSpeed
        }))
        .filter(item => item.position > -50)
    );
    
    // 발사체 업데이트
    setProjectiles(prev => 
      prev
        .map(projectile => ({
          ...projectile,
          position: {
            ...projectile.position,
            x: projectile.position.x + 10
          }
        }))
        .filter(projectile => projectile.position.x < window.innerWidth + 50)
    );
    
    // 충돌 감지
    detectCollisions();
    
    // 새 요소 생성
    createObstacle();
    createEnemy();
    createItem();
  }, [status, gameSpeed, createObstacle, createEnemy, createItem,detectCollisions]);
  

  // 게임 루프
  useEffect(() => {
    if (status === 'playing') {
      const gameLoop = setInterval(() => {
        updateGameElements();
      }, 1000 / 60); // 60 FPS
      
      return () => {
        clearInterval(gameLoop);
      };
    }
  }, [status, updateGameElements]);
  
  return {
    status,
    score,
    highScore,
    coins,
    totalCoins,
    specialMeter,
    specialActive,
    isJumping,
    isSliding,
    obstacles,
    enemies,
    items,
    projectiles,
    gameSpeed,
    startGame,
    pauseGame,
    endGame,
    jump,
    slide,
    attack,
    useSpecialAbility
  };
};
